import type { QuoteStatus, SignatureStatus } from '@worki/shared';

/**
 * HIST 2.8 / 2.10 — which send an item is offered: a first send, a resend while
 * it waits, a copy once it has been agreed to, or nothing.
 *
 * Worked out here once. Each screen with a send button used to carry its own
 * list of statuses, and the lists had drifted: one hid the resend on a locked
 * row and the next did not, and all of them still offered to resend an expired
 * item — which re-opened a lapsed price or set of terms for another full window.
 *
 * Expired is the status the server's sweep writes once a link lapses, the same
 * one the badge and the history's expired tab read. Not worked out again here
 * from the link's date: two rules for "expired" would disagree for the minute
 * between a link lapsing and the sweep noticing, and the badge would say one
 * thing while the menu did another.
 */
export type SendAction = 'send' | 'resend' | 'copy';

/*
 * Total records, so a status added to either list does not compile until
 * somebody has decided what may be sent from it.
 */
const QUOTE: Record<QuoteStatus, SendAction | null> = {
  draft: 'send',
  sent: 'resend',
  approved: 'copy',
  // Answered, and the answer was no: nothing to chase and nothing to copy.
  rejected: null,
  expired: null,
};

const DOCUMENT: Record<SignatureStatus, SendAction | null> = {
  draft: 'send',
  sent: 'resend',
  // Opened and not signed is still waiting — the case a resend exists for.
  viewed: 'resend',
  signed: 'copy',
  declined: null,
  cancelled: null,
  expired: null,
};

export function sendActionOf(kind: 'quote', item: { status: QuoteStatus; isLocked: boolean }): SendAction | null;
export function sendActionOf(kind: 'document', item: { status: SignatureStatus; isLocked: boolean }): SendAction | null;
export function sendActionOf(
  kind: 'quote' | 'document',
  item: { status: QuoteStatus | SignatureStatus; isLocked: boolean },
): SendAction | null {
  // Locked is locked to the customer: anything sent would open onto a page
  // they are not allowed to see, a copy included.
  if (item.isLocked) return null;
  return kind === 'quote'
    ? (QUOTE[item.status as QuoteStatus] ?? null)
    : (DOCUMENT[item.status as SignatureStatus] ?? null);
}
